<template>
  <!--
    Legacy Become-a-Mystic step: the flow is conceiving → deriving →
    published, so this only renders for an old saved draft.
    Browse (ready-made portrait pool) removed 2026-11-04 by Chris; the pool
    still exists server-side as a fallback; may return.
  -->
  <div class="image-step">
    <!-- Tab switcher -->
    <div class="tab-bar">
      <button class="tab-btn" :class="{ active: activeTab === 'advanced' }" @click="switchToAdvanced">Create Your Avatar</button>
    </div>

    <!-- ===================== CREATE YOUR AVATAR TAB ===================== -->
    <div v-if="activeTab === 'advanced'" class="create-tab">

      <!-- Accordion sections -->
      <div
        v-for="section in sections"
        :key="section.key"
        class="section-card"
        :class="{ active: activeSection === section.key }"
        @click="expandSection(section.key)"
      >
        <div class="section-header">
          <span class="section-label">{{ section.label }}</span>
          <span class="section-toggle">{{ activeSection === section.key ? '\u25B4' : '\u25BE' }}</span>
        </div>

        <!-- Expanded: full editable text -->
        <div v-if="activeSection === section.key" class="section-expanded">
          <div
            class="section-editable"
            contenteditable="true"
            @blur="onSectionBlur(section.textKey, $event)"
            @focus="onSectionFocus(section.key)"
            :key="section.key + '-' + descVersion"
            v-text="desc[section.textKey]"
          ></div>
        </div>
      </div>

      <!-- Paint + View Prompt buttons -->
      <div v-if="!generatedUrl" class="paint-bar">
        <button class="view-prompt-btn" @click="showPromptModal = true">View Prompt</button>
        <button class="paint-btn" :disabled="generating" @click="paintFromFields">
          {{ generating ? 'Painting...' : '\uD83C\uDFA8 Paint This' }}
        </button>
      </div>

      <!-- Result -->
      <div v-if="generatedUrl" class="result-section">
        <div class="result-frame">
          <img :src="generatedUrl" alt="Your portrait" class="result-img" />
        </div>
        <div class="result-actions">
          <button class="secondary-btn" @click="generatedUrl = ''">Refine &amp; try again</button>
          <button class="primary-btn" @click="acceptGenerated">This is me &rarr;</button>
        </div>
        <button class="view-prompt-link" @click="showPromptModal = true">View prompt that created this</button>
      </div>

      <!-- Prompt Modal -->
      <div v-if="showPromptModal" class="prompt-modal-overlay" @click.self="showPromptModal = false">
        <div class="prompt-modal">
          <div class="prompt-modal-header">
            <span>Full Illustrative Prompt</span>
            <button class="prompt-modal-close" @click="showPromptModal = false">&times;</button>
          </div>
          <div class="prompt-modal-body">{{ compiledDescription }}</div>
        </div>
      </div>
    </div>
  </div>
</template>

<script setup>
import { ref, reactive, onMounted, watch, computed } from 'vue'
import axios from 'axios'
import { static_store } from '@/store'

const props = defineProps({
  provisioningId: Number,
  portraitUrl: String,
  chosenTheme: { type: String, default: '' },
  chatAction: { type: Object, default: null },
})
const emit = defineEmits(['portrait-refreshed', 'next', 'context-signal'])

const activeTab = ref('advanced')

// ===================== SNIPPET TEMPLATES =====================

const ERA_SNIPPETS = {
  classic: 'A classic mystical portrait, rich oil-painted textures, ancient timeless aesthetic.',
  modern: 'A modern mystical portrait, clean digital art, contemporary ethereal feel.',
  futuristic: 'A futuristic mystical portrait, cosmic energy, neon accents, otherworldly technology.',
}
const ENVIRONMENT_SNIPPETS = {
  forest: 'Set in an ancient forest with warm lantern light, mist between trees, moss-covered stones.',
  coastal: 'Set on a coastal cliff, sea mist, tide pools reflecting moonlight, crashing waves.',
  desert: 'Set in sand dunes at night, campfire glow, star field above, sandstone arches.',
  garden: 'Set in an enchanted garden, flowering vines, stone fountain, fireflies, twilight glow.',
  mountain: 'Set on a mountain summit, alpine meadow, thin clouds, sunrise light.',
  underwater: 'Set in the deep ocean, bioluminescent light, coral formations, floating jellyfish.',
  volcanic: 'Set in a volcanic caldera, flowing lava rivers, obsidian formations, ember-lit smoke.',
  arctic: 'Set in the frozen tundra, aurora borealis, ice crystals, silver moonlight on snow.',
  jungle: 'Set in dense jungle canopy, ancient vines, exotic birds, filtered sunlight, waterfall.',
  meadow: 'Set in a wildflower meadow at golden hour, butterflies, gentle breeze, distant hills.',
  library: 'Set in a candlelit study, towering bookshelves, antique maps, warm firelight.',
  temple: 'Set in an ancient stone temple, incense smoke, carved pillars, sacred geometry, dawn light.',
  city: 'Set on an urban rooftop at dusk, city skyline, warm ambient light, string lights.',
  ruins: 'Set in overgrown ruins, crumbling columns, ivy and wildflowers reclaiming stone.',
  observatory: 'Set in an observatory dome, brass telescope, star charts on walls, night sky through open roof.',
  apothecary: 'Set in a cluttered apothecary, glass jars with herbs, mortar and pestle, candlelight.',
  throne_room: 'Set in a grand throne room, tapestries, stained glass, flickering torchlight.',
  celestial: 'Set on the celestial plane, floating among stars, moon halo, cosmic dust.',
  void: 'Set in the void, abstract darkness, faint geometric light, echo of stars.',
  aurora: 'Set beneath the aurora borealis, snow-dusted peaks, prismatic light.',
  eclipse: 'Set during a solar eclipse, corona of light, shadow and brilliance.',
  nebula: 'Set inside a nebula, gas clouds of pink and purple, newborn stars.',
}
const SUBJECT_SNIPPETS = {
  human: 'The subject is a human mystic, visible face and upper body, facing the viewer.',
  creature: 'The subject is a mystical creature - full form visible, centered composition.',
  mythical: 'The subject is a mythical being - elven, fae, or otherworldly, visible face, facing viewer.',
  object: 'The subject is a mystical object or symbol - centered, luminous, intricate detail.',
}
const GENDER_SNIPPETS = {
  male: 'Male-presenting, with strong defined features.',
  female: 'Female-presenting, with graceful elegant features.',
  neutral: 'Androgynous, with ambiguous ethereal features.',
}
const MOOD_SNIPPETS = {
  serene: 'Serene mood - peaceful, still, soft light, gentle expression.',
  fierce: 'Fierce mood - intense gaze, dramatic lighting, bold stance.',
  playful: 'Playful mood - mischievous energy, whimsical elements.',
  mysterious: 'Mysterious mood - half-shadowed, enigmatic, secrets in the eyes.',
  contemplative: 'Contemplative mood - inward gaze, quiet stillness.',
  radiant: 'Radiant mood - glowing aura, warm light, joyful presence.',
  dark: 'Dark mood - deep shadows, haunting beauty, twilight atmosphere.',
  warm: 'Warm mood - golden light, comforting presence, soft edges.',
}
const PALETTE_SNIPPETS = {
  warm_gold: 'Color palette: warm golds and ambers.',
  cool_blue: 'Color palette: cool blues and silvers.',
  earth: 'Color palette: earth tones and moss greens.',
  jewel: 'Color palette: jewel tones - ruby, sapphire, emerald.',
  purple: 'Color palette: deep purples and indigos.',
  sunset: 'Color palette: sunset oranges and pinks.',
  monochrome: 'Color palette: monochrome with gold accents.',
  pastel: 'Color palette: muted pastels.',
}
const STYLE_SNIPPETS = {
  oil_painting: 'Style: rich oil painting, visible brushstrokes, classical composition.',
  watercolor: 'Style: soft watercolor washes, bleeding edges, delicate translucency.',
  digital_art: 'Style: polished digital art, clean lines, vibrant saturation.',
  ethereal: 'Style: ethereal and dreamlike, soft focus, luminous.',
  dark_fantasy: 'Style: dark fantasy, dramatic chiaroscuro, ornate details.',
  art_nouveau: 'Style: art nouveau, flowing organic lines, Mucha-inspired.',
  impressionist: 'Style: impressionist, dappled light, loose brushwork.',
  surreal: 'Style: surreal dreamscape, impossible geometry.',
}

// Browse (ready-made portrait pool) removed 2026-11-04 by Chris: its filter
// options, grid, infinite scroll and choose-portrait claim went with it.

// ===================== ADVANCED STATE =====================

const desc = reactive({
  era: 'classic',
  eraText: ERA_SNIPPETS.classic + ' ' + STYLE_SNIPPETS.oil_painting,
  environment: '',
  environmentText: '',
  subjectType: 'human',
  subjectText: SUBJECT_SNIPPETS.human + ' ' + GENDER_SNIPPETS.neutral,
  mood: 'mysterious',
  moodText: MOOD_SNIPPETS.mysterious + ' ' + PALETTE_SNIPPETS.warm_gold,
  extraText: '',
})

const compiledDescription = computed(() => {
  return [desc.eraText, desc.environmentText, desc.subjectText, desc.moodText, desc.extraText]
    .filter(Boolean).join(' ') + ' No text. No logos. Square composition.'
})

// eslint-disable-next-line no-unused-vars
function onDropdownChange(field) {
  const map = { era: ERA_SNIPPETS, environment: ENVIRONMENT_SNIPPETS, subjectType: SUBJECT_SNIPPETS, gender: GENDER_SNIPPETS, mood: MOOD_SNIPPETS, palette: PALETTE_SNIPPETS, style: STYLE_SNIPPETS }
  const textKey = field + 'Text'
  const val = desc[field]
  if (map[field] && map[field][val]) { desc[textKey] = map[field][val] }
}

function switchToAdvanced() {
  activeTab.value = 'advanced'
  activeSection.value = 'era'
  suppressNextSectionFocus = true  // Don't fire section_focused — switched_to_create handles intro
  emit('context-signal', { panel: 'studio', view: 'image_step', interaction: 'switched_to_create', data: { section: 'era' } })
}

const generating = ref(false)
const generatedUrl = ref(props.portraitUrl || '')
const showPromptModal = ref(false)
const activeSection = ref('era')
const descVersion = ref(0)  // Bumped when LLM updates text to force contenteditable re-render
let suppressNextSectionFocus = false

const sections = [
  { key: 'era', label: 'Era & Style', textKey: 'eraText' },
  { key: 'environment', label: 'Environment', textKey: 'environmentText' },
  { key: 'subject', label: 'Subject & Gender', textKey: 'subjectText' },
  { key: 'mood', label: 'Mood & Palette', textKey: 'moodText' },
  { key: 'extra', label: 'Extra Details', textKey: 'extraText' },
]

// eslint-disable-next-line no-unused-vars
function truncate(text, len) {
  if (!text) return 'Tap to describe...'
  return text.length > len ? text.slice(0, len) + '...' : text
}

function expandSection(key) {
  // Don't collapse the active section — just switch to it
  if (activeSection.value === key) return
  activeSection.value = key
  if (suppressNextSectionFocus) {
    suppressNextSectionFocus = false
    return
  }
  emit('context-signal', {
    panel: 'studio', view: 'image_step',
    interaction: 'section_focused',
    data: {
      section: key,
      context: {
        era: desc.eraText,
        environment: desc.environmentText,
        subject: desc.subjectText,
        mood: desc.moodText,
        extra: desc.extraText,
      },
    },
  })
}

function onSectionBlur(textKey, event) {
  desc[textKey] = event.target.innerText
}

function onSectionFocus(key) {
  activeSection.value = key
}

// ===================== CHAT ACTION WATCHER =====================

watch(() => props.chatAction, (action) => {
  if (!action) return
  if (action.type === 'portrait_filter') {
    // Used to filter the Browse pool (removed 2026-11-04); the same picks
    // now shape the description to paint.
    activeTab.value = 'advanced'
    const f = action.filters || {}
    if (ERA_SNIPPETS[f.era]) { desc.era = f.era; desc.eraText = ERA_SNIPPETS[f.era] }
    if (ENVIRONMENT_SNIPPETS[f.theme]) { desc.environment = f.theme; desc.environmentText = ENVIRONMENT_SNIPPETS[f.theme] }
    if (SUBJECT_SNIPPETS[f.subject_type]) { desc.subjectType = f.subject_type; desc.subjectText = SUBJECT_SNIPPETS[f.subject_type] }
    if (GENDER_SNIPPETS[f.gender]) { desc.subjectText = [desc.subjectText, GENDER_SNIPPETS[f.gender]].filter(Boolean).join(' ') }
    descVersion.value++
  } else if (action.type === 'portrait_describe') {
    activeTab.value = 'advanced'
    desc.extraText = action.description || ''
  } else if (action.type === 'portrait_update_field' && action.textKey && action.value) {
    activeTab.value = 'advanced'
    desc[action.textKey] = action.value
    descVersion.value++  // Force contenteditable re-render
  } else if (action.type === 'portrait_focus_section' && action.section) {
    activeTab.value = 'advanced'
    activeSection.value = action.section
    emit('context-signal', { panel: 'studio', view: 'image_step', interaction: 'section_focused', data: { section: action.section } })
  } else if (action.type === 'portrait_paint') {
    paintFromFields()
  }
})

// ===================== API =====================

function authHeaders() {
  const token = localStorage.getItem('authToken')
  return token ? { Authorization: `Token ${token}` } : {}
}

async function paintFromFields() {
  generating.value = true
  generatedUrl.value = ''
  emit('context-signal', { panel: 'studio', view: 'image_step', interaction: 'painting_started', data: {} })
  try {
    const resp = await axios.post(
      `${static_store.apiUrl}/reader/become-mystic/refresh-portrait/${props.provisioningId}/`,
      { extra_description: compiledDescription.value },
      { headers: authHeaders() }
    )
    generatedUrl.value = resp.data.portrait_url
    emit('portrait-refreshed', resp.data.portrait_url)
    emit('context-signal', { panel: 'studio', view: 'image_step', interaction: 'painting_complete', data: { url: resp.data.portrait_url } })
  } catch {
    emit('context-signal', { panel: 'studio', view: 'image_step', interaction: 'painting_failed', data: {} })
  } finally { generating.value = false }
}

function acceptGenerated() { emit('next') }

// ===================== INIT =====================

onMounted(() => {
  if (props.chosenTheme) {
    desc.environment = props.chosenTheme
    desc.environmentText = ENVIRONMENT_SNIPPETS[props.chosenTheme] || ''
  }
})
</script>

<style scoped>
.image-step { color: #E8D8FF; }

/* Tab bar */
.tab-bar { display: flex; gap: 0; margin-bottom: 16px; border-bottom: 1px solid rgba(255,255,255,0.08); }
.tab-btn {
  flex: 1; padding: 10px; background: none; border: none; border-bottom: 2px solid transparent;
  color: rgba(195,169,255,0.4); font-size: 13px; font-family: 'Cinzel', serif; cursor: pointer; transition: all 0.2s;
}
.tab-btn:hover { color: rgba(195,169,255,0.7); }
.tab-btn.active { color: #C9A94E; border-bottom-color: #C9A94E; }

/* Create Your Avatar tab */
.create-tab { display: flex; flex-direction: column; gap: 6px; }

.section-card {
  background: rgba(255,255,255,0.03);
  border: 1px solid rgba(255,255,255,0.06);
  border-radius: 10px;
  cursor: pointer;
  transition: all 0.25s ease;
  overflow: hidden;
}
.section-card:hover { border-color: rgba(255,255,255,0.12); }
.section-card.active {
  border-color: rgba(201,169,78,0.4);
  box-shadow: 0 0 12px rgba(201,169,78,0.08);
  cursor: default;
}

.section-header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 10px 14px;
}
.section-label {
  font-size: 10px; text-transform: uppercase; letter-spacing: 0.08em;
  color: rgba(201,169,78,0.6); font-weight: 600;
}
.section-card.active .section-label { color: #C9A94E; }
.section-toggle { font-size: 12px; color: rgba(255,255,255,0.2); }

.section-preview {
  padding: 0 14px 10px;
  font-size: 13px; color: rgba(232,216,255,0.4);
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-style: italic;
  white-space: nowrap; overflow: hidden; text-overflow: ellipsis;
}

.section-expanded { padding: 0 14px 12px; }

.section-editable {
  font-size: 14px; color: rgba(232,216,255,0.85);
  font-family: 'Cormorant Garamond', Georgia, serif;
  font-style: italic; line-height: 1.7;
  outline: none; min-height: 40px;
  padding: 8px 10px;
  background: rgba(255,255,255,0.02);
  border-radius: 6px;
  border: 1px solid rgba(201,169,78,0.15);
}
.section-editable:focus {
  border-color: rgba(201,169,78,0.3);
  background: rgba(255,255,255,0.04);
}

.paint-bar { margin-top: 8px; display: flex; gap: 8px; align-items: stretch; }
.view-prompt-btn {
  padding: 12px 16px; background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.12);
  border-radius: 12px; color: rgba(195,169,255,0.5); font-size: 12px; cursor: pointer;
  font-family: inherit; white-space: nowrap; transition: all 0.2s;
}
.view-prompt-btn:hover { background: rgba(255,255,255,0.08); color: #E8D8FF; }
.view-prompt-link {
  background: none; border: none; color: rgba(195,169,255,0.4); font-size: 11px;
  cursor: pointer; text-decoration: underline; font-family: inherit; margin-top: 8px;
}
.view-prompt-link:hover { color: rgba(195,169,255,0.7); }

/* Prompt Modal */
.prompt-modal-overlay {
  position: fixed; inset: 0; background: rgba(0,0,0,0.7); z-index: 1000;
  display: flex; align-items: center; justify-content: center; padding: 20px;
}
.prompt-modal {
  background: #1a0a2e; border: 1px solid rgba(201,169,78,0.3); border-radius: 14px;
  max-width: 500px; width: 100%; max-height: 70vh; display: flex; flex-direction: column;
  box-shadow: 0 8px 40px rgba(0,0,0,0.5);
}
.prompt-modal-header {
  display: flex; justify-content: space-between; align-items: center;
  padding: 14px 18px; border-bottom: 1px solid rgba(255,255,255,0.06);
  font-size: 13px; color: #C9A94E; font-weight: 600; font-family: 'Cinzel', serif;
}
.prompt-modal-close {
  background: none; border: none; color: rgba(255,255,255,0.4); font-size: 20px;
  cursor: pointer; padding: 0 4px;
}
.prompt-modal-close:hover { color: #E8D8FF; }
.prompt-modal-body {
  padding: 18px; overflow-y: auto;
  font-size: 13px; line-height: 1.7; color: rgba(232,216,255,0.8);
  font-family: 'Cormorant Garamond', Georgia, serif; font-style: italic;
}
.paint-btn {
  flex: 1; padding: 16px;
  background: linear-gradient(135deg, rgba(201,169,78,0.15), rgba(201,169,78,0.08));
  border: 1px solid rgba(201,169,78,0.4);
  border-radius: 12px; color: #C9A94E;
  font-size: 16px; font-weight: 600;
  cursor: pointer; font-family: 'Cinzel', serif;
  letter-spacing: 0.03em;
  transition: all 0.2s;
}
.paint-btn:hover:not(:disabled) {
  background: linear-gradient(135deg, rgba(201,169,78,0.25), rgba(201,169,78,0.12));
  box-shadow: 0 0 20px rgba(201,169,78,0.12);
}
.paint-btn:disabled { opacity: 0.4; cursor: not-allowed; }

/* Result */
.result-section { display: flex; flex-direction: column; align-items: center; gap: 12px; padding: 8px 0; }
.result-frame {
  width: 200px; height: 200px; border-radius: 14px; overflow: hidden;
  border: 2px solid rgba(201,169,78,0.3); box-shadow: 0 4px 30px rgba(0,0,0,0.4);
}
.result-img { width: 100%; height: 100%; object-fit: cover; }
.result-actions { display: flex; gap: 10px; width: 100%; }

/* Shared buttons */
.primary-btn {
  flex: 1; width: 100%; padding: 12px;
  background: rgba(201,169,78,0.12); border: 1px solid rgba(201,169,78,0.35);
  border-radius: 10px; color: #C9A94E; font-size: 14px; font-weight: 500;
  cursor: pointer; font-family: 'Cinzel', serif; transition: all 0.2s;
}
.primary-btn:hover:not(:disabled) { background: rgba(201,169,78,0.22); }
.primary-btn:disabled { opacity: 0.4; cursor: not-allowed; }
.secondary-btn {
  flex: 1; padding: 12px;
  background: rgba(255,255,255,0.04); border: 1px solid rgba(255,255,255,0.12);
  border-radius: 10px; color: rgba(255,255,255,0.5); font-size: 13px;
  cursor: pointer; font-family: inherit;
}
.secondary-btn:hover { background: rgba(255,255,255,0.08); color: #E8D8FF; }
</style>
